import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { ChevronDown } from 'lucide-react'
import { EventInterestIcon } from './EventInterestIcon'
import { EventInterestMenuItems } from './EventInterestMenuItems'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import { cn } from '../lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import type { EventInterestStatus } from '../hooks/useUserEventInterests'

function statusLabel(status: EventInterestStatus): string {
  if (status === 'going') return fbt('Going', 'Attendance dropdown label when the user is going')
  if (status === 'interested') {
    return fbt('Interested', 'Attendance dropdown label when the user is interested')
  }
  if (status === 'not_going') {
    return fbt('Not going', 'Attendance dropdown label when the user has declined the event')
  }
  return fbt('Are you going?', 'Attendance dropdown label before the user has responded')
}

export type AttendanceMenuVariant = 'row' | 'overlay' | 'icon' | 'circle'

/**
 * The one control for answering "are you going" — every RSVP surface in the app
 * is this component in a different trigger shape, so the options, their order
 * and their icons only exist in one place.
 *
 * `row` is a labeled full-width button; `overlay` is a bare icon floating over
 * an event's cover, where a label — or a chrome disc — would cover up the art;
 * `icon` is the compact action tucked into an event card's corner; `circle` is
 * a big FAB-style disc that overlaps a party's cover as its primary RSVP, with
 * a roomier dropdown to match.
 */
export function EventAttendanceMenu({
  status,
  onSetStatus,
  variant = 'row',
  onDarkBackground = false,
  footer,
  loading = false,
}: {
  status: EventInterestStatus
  onSetStatus: (status: EventInterestStatus) => void
  variant?: AttendanceMenuVariant
  /** `icon` only: lighten the resting colour for a card with cover art. */
  onDarkBackground?: boolean
  /**
   * We don't know this viewer's answer yet — auth is still resolving, or their
   * RSVPs haven't arrived. Renders a skeleton rather than an armed control:
   * `none` would read as "hasn't answered" for someone who already has, and an
   * answer given before the uid exists is written nowhere.
   */
  loading?: boolean
  /**
   * Extra items below a separator, for a card whose menu does more than RSVP
   * (a group's linked event can also be unlinked).
   */
  footer?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const lastPointerType = useRef<string | null>(null)

  // Radix opens on pointerdown, so a scroll that starts on the trigger opens
  // the menu. For touch, skip Radix's handler and open on click instead — the
  // browser never fires click for a gesture that turned into a scroll.
  const touchOpenHandlers = {
    onPointerDown: (e: PointerEvent) => {
      lastPointerType.current = e.pointerType
      if (e.pointerType !== 'mouse') e.preventDefault()
    },
    onClick: () => {
      if (lastPointerType.current && lastPointerType.current !== 'mouse') {
        setOpen(!open)
      }
      lastPointerType.current = null
    },
  }

  if (loading) {
    if (variant === 'icon') return <Skeleton className="size-7 rounded-full" />
    if (variant === 'circle') {
      return <Skeleton className="size-14 rounded-full ring-4 ring-background" />
    }
    if (variant === 'overlay') {
      return (
        <div className="flex size-11 items-center justify-center">
          <Skeleton className="size-8 rounded-full" />
        </div>
      )
    }
    return <Skeleton className="h-9 w-full rounded-md" />
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
    >
      <DropdownMenuTrigger asChild {...touchOpenHandlers}>
        {variant === 'icon' ? (
          // A plain button, not <Button>: this sits in a card corner and must
          // stay flush with the card's own padding.
          <button
            type="button"
            // The card behind it navigates on click.
            onClick={e => e.stopPropagation()}
            aria-label={statusLabel(status)}
            title={statusLabel(status)}
            className={cn(
              'cursor-pointer p-1 -m-1 rounded transition-colors',
              status === 'going'
                ? 'text-going hover:text-going/80'
                : status === 'interested'
                  ? 'text-interested hover:text-interested/80'
                  : onDarkBackground
                    ? 'text-on-image-muted hover:text-on-image'
                    : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <EventInterestIcon status={status} className="size-7" />
          </button>
        ) : variant === 'circle' ? (
          // A big FAB-style disc that overlaps a party's cover — its own primary
          // RSVP. The ring lifts it off the art; an unanswered event fills with
          // the primary colour to invite a first tap, then goes neutral so the
          // status icon's own colour reads.
          <Button
            size="icon"
            variant={status === 'none' ? 'default' : 'secondary'}
            aria-label={statusLabel(status)}
            title={statusLabel(status)}
            className="size-14 rounded-full shadow-lg ring-4 ring-background"
          >
            <EventInterestIcon status={status} className="size-7" />
          </Button>
        ) : variant === 'overlay' ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={statusLabel(status)}
            title={statusLabel(status)}
            className={cn(
              // Nothing but the icon — the ghost variant's hover/open fill and
              // the focus ring would both draw a chip around it over the art.
              'size-11 drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]',
              'hover:bg-transparent aria-expanded:bg-transparent focus-visible:border-transparent focus-visible:ring-0',
              status === 'none' && 'text-on-image hover:text-on-image aria-expanded:text-on-image',
            )}
          >
            <EventInterestIcon status={status} className="size-8" />
          </Button>
        ) : (
          <Button
            variant={status === 'none' ? 'default' : 'outline'}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2">
              <EventInterestIcon status={status} className="size-5" />
              {statusLabel(status)}
            </span>
            <ChevronDown className="size-4 opacity-70" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === 'icon' || variant === 'circle' ? 'end' : 'start'}
        onClick={e => e.stopPropagation()}
        className={cn(
          variant === 'row' && 'w-[var(--radix-dropdown-menu-trigger-width)]',
          variant === 'circle' && 'min-w-52',
        )}
      >
        <EventInterestMenuItems
          status={status}
          onSetStatus={onSetStatus}
          size={variant === 'circle' ? 'lg' : 'default'}
        />
        {footer && (
          <>
            <DropdownMenuSeparator />
            {footer}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
